// Helpers for the command catalog served by GET /device-groups/command-types.
//
// The endpoint returns { commandTypes, commands, optionSources }. `commands`
// describes each command's payload fields and `optionSources` says where a
// field's dropdown options come from, so the send dialogs can render themselves
// instead of hardcoding a form per command type.

// Option-source urls come back as absolute api paths ("/api/device-groups/..."),
// but the axios instance is already based at <origin>/api/ -- strip the prefix
// so the two do not stack into /api/api/.
export function toRelativeApiPath (url) {
  return String(url || '').replace(/^\/?api\/?/i, '')
}

function readValue (payload, key) {
  if (!payload || !key) {
    return undefined
  }

  if (payload[key] !== undefined) {
    return payload[key]
  }

  // Field keys are PascalCase while url placeholders are camelCase.
  const wanted = String(key).toLowerCase()
  const match = Object.keys(payload).find(entry => entry.toLowerCase() === wanted)
  return match === undefined ? undefined : payload[match]
}

export function payloadValue (payload, key) {
  return readValue(payload, key)
}

const PLACEHOLDER = /\{(\w+)\}/g

// Fills {companyId} / {packageName} / ... from the company and the payload built
// so far. Returns `null` when a placeholder has no value yet, which is the
// signal that a dependent dropdown is not ready to load.
export function resolveOptionsUrl (template, { companyId, payload, field } = {}) {
  let missing = false

  const url = String(template || '').replace(PLACEHOLDER, (_match, name) => {
    let value

    if (name.toLowerCase() === 'companyid') {
      value = companyId
    } else if (field && field.dependsOn && readValue(payload, field.dependsOn) !== undefined) {
      value = readValue(payload, field.dependsOn)
    } else {
      value = readValue(payload, name)
    }

    if (value === undefined || value === null || value === '') {
      missing = true
      return ''
    }

    return encodeURIComponent(value)
  })

  return missing ? null : url
}

function normalizeField (field) {
  if (!field || !field.key) {
    return null
  }

  return {
    key: field.key,
    label: field.label || field.key,
    input: String(field.input || 'text').toLowerCase(),
    required: !!field.required,
    default: field.default === undefined ? null : field.default,
    optionsSource: field.optionsSource || null,
    itemText: field.itemText || null,
    itemValue: field.itemValue || null,
    dependsOn: field.dependsOn || null,
    help: field.help || null,
    placeholder: field.placeholder || null,
    // The backend fills these in itself (job ids, timestamps, download urls).
    serverDerived: !!field.serverDerived,
  }
}

function normalizeCommand (command) {
  if (!command || !command.type) {
    return null
  }

  return {
    type: command.type,
    label: command.label || command.type,
    description: command.description || '',
    payloadShape: String(command.payloadShape || 'object').toLowerCase(),
    fields: (Array.isArray(command.fields) ? command.fields : [])
      .map(normalizeField)
      .filter(Boolean),
  }
}

function normalizeOptionSource (source) {
  if (!source || !source.name) {
    return null
  }

  return {
    name: source.name,
    url: source.url || '',
    itemText: source.itemText || null,
    itemValue: source.itemValue || null,
    dependsOnQueryParameter: source.dependsOnQueryParameter || null,
  }
}

function normalizeExpiry (entry) {
  if (!entry || !entry.type) {
    return null
  }

  const minutes = Number(entry.defaultTtlMinutes)

  return {
    type: entry.type,
    defaultTtlMinutes: Number.isFinite(minutes) ? minutes : null,
    neverExpiresByDefault: !!entry.neverExpiresByDefault,
  }
}

// Human form of a minute count: 30 -> "30 min", 120 -> "2 h", 10080 -> "7 d".
export function formatMinutes (minutes) {
  const value = Number(minutes)
  if (!Number.isFinite(value) || value <= 0) {
    return ''
  }
  if (value % 1440 === 0) {
    const days = value / 1440
    return `${days} day${days === 1 ? '' : 's'}`
  }
  if (value % 60 === 0) {
    const hours = value / 60
    return `${hours} hour${hours === 1 ? '' : 's'}`
  }
  return `${value} min`
}

export function describeDefaultExpiry (info) {
  if (!info) {
    return 'Use the server default'
  }
  if (info.neverExpiresByDefault || !info.defaultTtlMinutes) {
    return 'Server default (never expires)'
  }
  return `Server default (expires in ${formatMinutes(info.defaultTtlMinutes)})`
}

// Turns the picker's state into the fields the send endpoint expects. Sending
// neither lets the server apply its per-type default.
export function buildExpiryFields (expiry) {
  if (!expiry || expiry.mode === 'default') {
    return {}
  }

  if (expiry.mode === 'ttl') {
    const minutes = Number(expiry.ttlMinutes)
    return Number.isFinite(minutes) && minutes > 0 ? { ttlMinutes: minutes } : {}
  }

  if (expiry.mode === 'absolute' && expiry.expiresAt) {
    return { expiresAt: expiry.expiresAt }
  }

  return {}
}

// The single-device endpoints take only ttlMinutes, so an absolute pick is
// converted to minutes from now. Returns null to mean "use the server default".
export function buildTtlMinutes (expiry) {
  if (!expiry || expiry.mode === 'default') {
    return null
  }

  if (expiry.mode === 'ttl') {
    const minutes = Number(expiry.ttlMinutes)
    return Number.isFinite(minutes) && minutes > 0 ? minutes : null
  }

  if (expiry.mode === 'absolute' && expiry.expiresAt) {
    const at = new Date(expiry.expiresAt).getTime()
    if (Number.isNaN(at)) {
      return null
    }
    const minutes = Math.round((at - Date.now()) / 60000)
    return minutes > 0 ? minutes : 1
  }

  return null
}

// Accepts the new catalog object and the old bare string array, so an older
// backend still yields a usable (if formless) type list.
export function normalizeCatalog (data) {
  const empty = { commandTypes: [], commands: [], commandsByType: {}, optionSources: {}, expiryByType: {} }

  if (!data) {
    return empty
  }

  if (Array.isArray(data)) {
    const commands = data
      .filter(entry => typeof entry === 'string')
      .map(type => ({ type, label: type, description: '', payloadShape: 'none', fields: [] }))

    return {
      commandTypes: commands.map(command => command.type),
      commands,
      commandsByType: commands.reduce((all, command) => ({ ...all, [command.type]: command }), {}),
      optionSources: {},
      expiryByType: {},
    }
  }

  const commands = (Array.isArray(data.commands) ? data.commands : [])
    .map(normalizeCommand)
    .filter(Boolean)

  const optionSources = (Array.isArray(data.optionSources) ? data.optionSources : [])
    .map(normalizeOptionSource)
    .filter(Boolean)
    .reduce((all, source) => ({ ...all, [source.name]: source }), {})

  const commandsByType = commands.reduce((all, command) => ({ ...all, [command.type]: command }), {})

  const commandTypes = Array.isArray(data.commandTypes) && data.commandTypes.length
    ? data.commandTypes.filter(type => typeof type === 'string')
    : commands.map(command => command.type)

  const expiryByType = (Array.isArray(data.expiry) ? data.expiry : [])
    .map(normalizeExpiry)
    .filter(Boolean)
    .reduce((all, entry) => ({ ...all, [entry.type]: entry }), {})

  return { commandTypes, commands, commandsByType, optionSources, expiryByType }
}

// Items for a v-select: the label to show, the type to send.
export function commandSelectItems (catalog) {
  const byType = (catalog && catalog.commandsByType) || {}

  return ((catalog && catalog.commandTypes) || []).map(type => {
    const command = byType[type]
    return {
      value: type,
      text: command ? command.label : type,
      description: command ? command.description : '',
    }
  })
}

// Fields the operator actually fills in.
export function editableFields (command) {
  if (!command || command.payloadShape === 'none') {
    return []
  }
  return command.fields.filter(field => !field.serverDerived)
}

// Turns the collected field values into the payload the command actually wants.
// `payloadShape` decides the envelope:
//   none   -> no payload at all
//   scalar -> the single field's bare value (Reboot sends 5, not { seconds: 5 })
//   object -> an object keyed by the catalog's declared field keys
// Values may be loosely keyed (camelCase, from the bespoke Devices dialogs);
// lookups are case-insensitive so they land on the declared keys either way.
// True when the command declares fields but none produced a value, i.e. posting
// would send `payload: null` into a JsonElement the server treats as required.
export function isPayloadIncomplete (command, values) {
  if (!command || command.payloadShape === 'none') {
    return false
  }

  const built = buildPayloadForCommand(command, values)

  if (built === null || built === undefined) {
    return editableFields(command).length > 0
  }

  if (typeof built === 'object') {
    return Object.keys(built).length === 0
  }

  return false
}

export function buildPayloadForCommand (command, values) {
  if (!command || command.payloadShape === 'none') {
    return null
  }

  const fields = editableFields(command)
  if (!fields.length) {
    return command.payloadShape === 'scalar' ? null : (values || null)
  }

  const valueFor = field => {
    const value = readValue(values, field.key)
    if (value !== undefined) {
      return value
    }
    return field.default === undefined ? null : field.default
  }

  if (command.payloadShape === 'scalar') {
    return valueFor(fields[0])
  }

  const payload = {}
  fields.forEach(field => {
    payload[field.key] = valueFor(field)
  })

  return payload
}

function optionIdentity (option, valueKey) {
  if (option === null || option === undefined) {
    return null
  }
  if (typeof option !== 'object') {
    return option
  }
  const value = readValue(option, valueKey)
  return value === undefined ? null : value
}

// A device group can span companies, and an app list is per company. Keep only
// the options every company in the group has, so an operator cannot pick an app
// that half the group's devices have never installed.
export function intersectOptionLists (lists, valueKey) {
  const usable = (lists || []).filter(Array.isArray)

  if (!usable.length) {
    return { options: [], meta: { companyCount: 0, listSizes: [], commonCount: 0 } }
  }

  const listSizes = usable.map(list => list.length)

  if (usable.length === 1) {
    return {
      options: usable[0],
      meta: { companyCount: 1, listSizes, commonCount: usable[0].length },
    }
  }

  const keySets = usable.map(list => new Set(
    list.map(option => optionIdentity(option, valueKey)).filter(key => key !== null),
  ))

  const seen = new Set()
  const options = usable[0].filter(option => {
    const key = optionIdentity(option, valueKey)
    if (key === null || seen.has(key)) {
      return false
    }
    if (!keySets.every(set => set.has(key))) {
      return false
    }
    seen.add(key)
    return true
  })

  return {
    options,
    meta: { companyCount: usable.length, listSizes, commonCount: options.length },
  }
}

export default {
  toRelativeApiPath,
  formatMinutes,
  describeDefaultExpiry,
  buildExpiryFields,
  buildTtlMinutes,
  intersectOptionLists,
  isPayloadIncomplete,
  payloadValue,
  resolveOptionsUrl,
  normalizeCatalog,
  commandSelectItems,
  editableFields,
  buildPayloadForCommand,
}
